import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { useCurveStore } from '@/stores/useCurveStore';
import { Save, RotateCcw, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const PRODUCT_TYPES = ['generic', 'microloan', 'retail', 'hp', 'payday'];

export const CurveActions = () => {
  const { hasChanges, loading, save, reset, resetToDefaults, copyCurve, product } = useCurveStore();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyFromProduct, setCopyFromProduct] = useState('');

  const handleSave = async () => {
    try {
      await save();
      toast.success('Curve saved successfully');
    } catch (error) {
      toast.error('Failed to save curve');
    }
  };

  const handleResetToDefaults = async () => {
    try {
      await resetToDefaults();
      setShowResetDialog(false);
      toast.success('Curve reset to defaults');
    } catch (error) {
      toast.error('Failed to reset curve');
    }
  };

  const handleCopy = async () => {
    if (!copyFromProduct) return;
    
    try {
      await copyCurve(copyFromProduct);
      setShowCopyDialog(false);
      setCopyFromProduct('');
      toast.success(`Copied curve from ${copyFromProduct}`);
    } catch (error) {
      toast.error('Failed to copy curve');
    }
  };

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <motion.div
              animate={hasChanges ? { scale: [1, 1.05, 1] } : {}}
              transition={{ repeat: hasChanges ? Infinity : 0, duration: 2 }}
            >
              <Button
                onClick={handleSave}
                disabled={!hasChanges || loading}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </motion.div>

            <Button
              onClick={reset}
              disabled={!hasChanges || loading}
              variant="outline"
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Undo Changes
            </Button>

            <Button
              onClick={() => setShowResetDialog(true)}
              disabled={loading}
              variant="destructive"
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Reset to Defaults
            </Button>

            <Button
              onClick={() => setShowCopyDialog(true)}
              disabled={loading}
              variant="secondary"
              className="gap-2"
            >
              <Copy className="h-4 w-4" />
              Duplicate from...
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default Curve?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the default recovery curve for {product}. 
              All current customizations will be lost. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetToDefaults}>
              Reset to Defaults
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Dialog */}
      <AlertDialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copy Curve from Another Product</AlertDialogTitle>
            <AlertDialogDescription>
              Select a product type to copy its recovery curve to {product}.
              This will replace the current curve.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <Select value={copyFromProduct} onValueChange={setCopyFromProduct}>
            <SelectTrigger>
              <SelectValue placeholder="Select product type" />
            </SelectTrigger>
            <SelectContent>
              {PRODUCT_TYPES.filter(p => p !== product).map(type => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCopy} disabled={!copyFromProduct}>
              Copy Curve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
